import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import styled from 'styled-components'
import { mapErrorsToString } from '../../utils/Errors'
import LoadingIndicator from '../LoadingIndicator'

const StyledDropzone = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    & > label:first-child {
        font-weight: 700;
    }
    & > label {
        margin-bottom: 25px;
    }
    border-radius: 5px;
    border: 2px dashed #c9c9c9;
    height: 120px;
    &.invalid {
        background: rgba(255, 0, 0, 0.1);
        border: 2px dashed #FF0000;
    }
`

const UploadMessage = styled.div`
    text-align: center;
    color: #FF0000;
`

const PlaceholderTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    color: #727272;
    margin-bottom: 7.5px;
`

const PlaceholderText = styled.div`
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    color: #727272;
    margin-top: 7.5px;
    padding: 0 40px;
`

interface DropzoneProps {
    accept: string
    placeholderTitle: string
    placeholderText: string
    readonly?: boolean
    onDropFile: (file: File, onUploadProgress: (progress: number) => void, onSuccessCallback: () => void, onErrorCallback: (reason: string) => void) => void
}

const Dropzone: React.FC<DropzoneProps> = ({ accept, placeholderTitle, placeholderText, readonly, onDropFile }) => {
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const [progress, setProgress] = useState(0)

    const onUploadProgress = useCallback((progress: number) => {
        setProgress(progress)
    }, [])
    const onSuccessCallback = useCallback(() => {
        setUploading(false)
        setError(undefined)
    }, [])
    const onErrorCallback = useCallback((reason: string) => {
        setUploading(false)
        setError(mapErrorsToString(reason))
    }, [])
    const onDrop = useCallback((files: File[]) => {
        setError(undefined)
        setUploading(true)
        if (files && files.length > 0) {
            onDropFile(files[0], onUploadProgress, onSuccessCallback, onErrorCallback)
        } else {
            onErrorCallback('Invalid file or file type selected')
        }
    }, [onDropFile, onErrorCallback, onSuccessCallback, onUploadProgress])

    const { getRootProps, getInputProps } = useDropzone({ onDrop, accept, disabled: readonly, multiple: false })
    return (
        <StyledDropzone {...getRootProps({ refKey: 'innerRef' })} className={error ? "invalid" : ""} style={{ position: "relative" }} >
            <input {...getInputProps()} />
            {error && <UploadMessage>{error}</UploadMessage>}
            {!error && <PlaceholderTitle>{placeholderTitle}</PlaceholderTitle>}
            {<PlaceholderText>{placeholderText}</PlaceholderText>}
            {uploading && <LoadingIndicator progress={progress} />}
        </StyledDropzone>
    )
}

export default Dropzone